import type {Lead} from 'types/entities/Lead'
import type {CompanyAddress} from 'types/entities/CompanyAddress'
import {getLeadCompanyRegion, getPrimaryCompanyAddress} from 'utils/companiesUtils'
import {opportunityAddressKey} from 'utils/leads/opportunityCantonSnapshot'

export interface CantonPersistenceApi {
    getLead: (id: number) => Promise<Lead>
    getAddress: (id: number) => Promise<CompanyAddress>
    saveAddress: (address: CompanyAddress) => Promise<CompanyAddress>
}

/** Recheck the address after Google responds; never save the stale table snapshot. */
export const persistOpportunityCanton = async (
    original: Lead, region: string, api: CantonPersistenceApi
): Promise<Lead | null> => {
    const fresh = await api.getLead(original.id)
    if (fresh.source !== original.source || opportunityAddressKey(fresh) !== opportunityAddressKey(original)) return null
    if (getLeadCompanyRegion(fresh)) return fresh
    const primary = fresh.company && getPrimaryCompanyAddress(fresh.company)
    // Legacy scalar addresses need backend migration before they can be updated safely.
    if (!primary?.id || !fresh.company?.id) return null
    const address = await api.getAddress(primary.id)
    if (address.id !== primary.id || address.company_id !== fresh.company.id || address.deleted_at ||
        ['street_name', 'postal_code', 'town', 'country'].some(field =>
            address[field as keyof CompanyAddress] !== primary[field as keyof CompanyAddress])) return null
    if (address.region?.trim()) {
        return {...fresh, company: {...fresh.company,
            addresses: fresh.company.addresses?.map(item => item.id === address.id ? address : item)}}
    }
    const saved = await api.saveAddress({...address, region})
    if (saved.id !== address.id || saved.company_id !== address.company_id || saved.region !== region) return null
    return {...fresh, state: region, company: {...fresh.company,
        addresses: fresh.company.addresses?.map(item => item.id === saved.id ? saved : item)}}
}
